// Detailed view for editing and viewing task hierarchy.
import { useState, useMemo, useRef, useLayoutEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography, IconButton } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

import { useTasksContext } from '@/features/tasks/context/TasksContext.def';
import SubtasksPanel from '@/features/tasks/components/lists/TaskGroupSubtasks';
import useAddSubtask from '@/features/tasks/hooks/mutations/use-task-add-subtask.js';
import TaskDescription from './TaskDescription';

import '@/styles/prose-content.css';
import { useT } from '@/i18n/useT';

const getDirectSubtasks = (task, tasks) => {
  if (!task?.materializedPath) return [];
  const parentPath = task.materializedPath;
  return (
    tasks?.filter(
      (t) => t.materializedPath.startsWith(parentPath) && t.id !== task.id,
    ) || []
  );
};

const sortSubtasks = (subtasks) => {
  return subtasks.sort((a, b) => {
    if (a.completed !== b.completed)
      return Number(a.completed) - Number(b.completed);
    const pathA = a.materializedPath || '';
    const pathB = b.materializedPath || '';
    if (pathA < pathB) return -1;
    if (pathA > pathB) return 1;
    return 0;
  });
};

const formatTagCaption = (task, projectDisplayMap, contextDisplayMap) => {
  const projects = (task.projects || []).map(
    (id) => projectDisplayMap.get(String(id).toLowerCase()) || id,
  );
  const contexts = (task.contexts || []).map((id) => {
    const label = contextDisplayMap.get(String(id).toLowerCase()) || id;
    return String(label).startsWith('@') ? label : `@${label}`;
  });
  return [...projects, ...contexts].filter(Boolean).join(' · ');
};

const OrganizeDetailPane = ({
  task,
  tasks,
  onUpdate,
  duplicateOrderKeyTaskIds,
  plannedParentIds,
  waitingForParentIds,
  showCompleted = true,
}) => {
  const t = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const { projectDisplayMap, contextDisplayMap } = useTasksContext();
  const { handleSaveSubtask } = useAddSubtask(task, tasks, onUpdate);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const descriptionRef = useRef(null);

  const subtasks = useMemo(() => {
    const directSubtasks = getDirectSubtasks(task, tasks);
    return sortSubtasks(directSubtasks);
  }, [tasks, task]);

  const { description } = task || {};
  const tagCaption = task
    ? formatTagCaption(task, projectDisplayMap, contextDisplayMap)
    : '';

  useLayoutEffect(() => {
    setIsExpanded(false);
    if (!description) {
      setIsOverflowing(false);
      return;
    }
    const checkOverflow = () => {
      if (descriptionRef.current) {
        setIsOverflowing(
          descriptionRef.current.scrollHeight >
            descriptionRef.current.clientHeight,
        );
      }
    };
    const timer = setTimeout(checkOverflow, 50);
    return () => clearTimeout(timer);
  }, [description]);

  if (!task) {
    return (
      <Box sx={{ p: 3, color: 'text.subtle' }}>
        <Typography variant="body2">
          {t('organize.selectTask', 'Select a task to see details')}
        </Typography>
      </Box>
    );
  }

  const handleEdit = () => navigate(`${location.pathname}?task=${task.id}`);

  return (
    <Box
      sx={{
        p: 2,
        bgcolor: 'background.subtle',
        minHeight: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ flexShrink: 0 }}>
        <Box
          sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.5 }}
        >
          <Typography
            variant="subtitle1"
            component="h2"
            data-testid="organize-detail-parent-title"
            sx={{
              flex: 1,
              fontWeight: 600,
              lineHeight: 1.35,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {task.title}
          </Typography>
          <IconButton
            size="small"
            aria-label={t('organize.editTask', 'Edit task')}
            title={t('organize.editTask', 'Edit task')}
            onClick={handleEdit}
            tabIndex={-1}
          >
            <EditOutlinedIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
        {tagCaption ? (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mb: 1 }}
          >
            {tagCaption}
          </Typography>
        ) : null}
        <TaskDescription
          description={description}
          isExpanded={isExpanded}
          setIsExpanded={setIsExpanded}
          isOverflowing={isOverflowing}
          descriptionRef={descriptionRef}
        />
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto' }} data-testid="subtasks-section">
        <SubtasksPanel
          parentId={task.id}
          subtasks={subtasks}
          onSaveSubtask={handleSaveSubtask}
          isSortable={true}
          droppableId={`subtasks-of-${task.id}`}
          droppableData={{ type: 'subtask-area', parentTask: task }}
          duplicateOrderKeyTaskIds={duplicateOrderKeyTaskIds}
          plannedParentIds={plannedParentIds}
          waitingForParentIds={waitingForParentIds}
          emptyStateMessage={t(
            'organize.noSubtasks',
            'Drag a task here',
          )}
          showCompleted={showCompleted}
          closeOnSave={true}
        />
      </Box>
    </Box>
  );
};

export default OrganizeDetailPane;
